/**
 * Cliente de `GET /api/search` — a busca global (Story 18.1, PR B).
 *
 * ⚠️ ESTES TIPOS SÃO UM ESPELHO, NÃO UMA INVENÇÃO. Cada um deles foi lido de
 * `apps/api/src/lib/global-search.ts` (o contrato do PR A) e copiado com o mesmo
 * nome e a mesma forma. Não há um pacote partilhado entre `apps/web` e
 * `apps/api` para tipos de resposta HTTP — o resto desta pasta faz exactamente
 * isto (ver `lib/api/job-profiles.ts`), e a alternativa (importar do worker)
 * arrastaria `drizzle` e `hono` para dentro do bundle do SPA.
 *
 * O QUE ESTE MÓDULO NÃO TEM, E POR QUÊ
 * ------------------------------------
 * Não tem `score`. A variante escolhida pelo owner tem "mérito na linha:
 * nenhum" — nada na paleta desenha nota, e é por isso que a rota também não a
 * devolve. Se um dia alguém a acrescentar aqui, o campo nasce morto e vai
 * divergir do `applicationScore()` das listagens.
 */
import { API_BASE } from '@/lib/api'
import type { ApplicationStatus } from '@/lib/api/applications'
import { readProblemMessage } from '@/lib/api/problem'

/**
 * As seis entidades, NA ORDEM DO CONTRATO.
 *
 * A ordem é uma decisão de produto (candidatura primeiro) e viaja do servidor
 * para o cliente dentro do array `groups`. Esta constante existe para o TRILHO
 * de escopo: ele precisa de desenhar as seis, mesmo as que vieram vazias, e o
 * `⌘1…⌘6` é indexado por ela.
 */
export const SEARCH_KINDS = [
  'application',
  'professional',
  'opportunity',
  'customer',
  'jobProfile',
  'assessment',
] as const

export type SearchKind = (typeof SEARCH_KINDS)[number]

/** O que TODA linha da paleta tem, seja de que entidade for. */
interface BaseSearchItem {
  id: string
  lead: string
  sub: string | null
  /** Uma rota REAL do `App.tsx` — quem a monta é o servidor. */
  path: string
}

/**
 * A linha de uma CANDIDATURA — a única com etapa.
 *
 * Os nomes são os que `apps/api/src/routes/applications.ts` já emite. Renomear
 * um campo do contrato HTTP é o erro que já custou um 400 com o CI verde neste
 * repositório.
 *
 * `status` é o eixo de DESFECHO e não se confunde com a etapa: a etapa diz onde
 * o processo vai, o estado diz se ainda vai. Reutiliza o `ApplicationStatus` da
 * lista — assim o rótulo e o tom saem do `statusMeta()` canónico, e a busca não
 * pode passar a chamar "Reprovada" outra coisa que a lista chame diferente.
 */
export type ApplicationSearchItem = BaseSearchItem & {
  kind: 'application'
  currentStageName: string | null
  currentStagePosition: number | null
  status: ApplicationStatus
}

/** A linha de qualquer uma das outras cinco — sem etapa, porque não têm. */
export type PlainSearchItem = BaseSearchItem & {
  kind: Exclude<SearchKind, 'application'>
}

/**
 * Uma linha da paleta — UNIÃO DISCRIMINADA, e não uma interface com dois campos
 * opcionais.
 *
 * O `kind` viaja no ITEM (e não só no grupo) porque a navegação por teclado é
 * sobre uma lista PLANA que atravessa os grupos: no instante em que uma linha é
 * aberta, o grupo já não está à mão. O cliente precisa do `kind` três vezes na
 * mesma linha — o glifo da entidade, a etapa (só a candidatura a tem) e o
 * `kind:id` que o histórico grava.
 */
export type SearchItem = ApplicationSearchItem | PlainSearchItem

/**
 * Um grupo da resposta.
 *
 * `failed` separa "não encontrei nada" de "não cheguei a saber". Sem ele, o
 * trilho de escopo escreveria "Clientes: 0, desativado" sobre uma entidade que
 * nunca chegou a responder — afirmar zero sobre o que se desconhece.
 */
export interface SearchGroup {
  kind: SearchKind
  hasMore: boolean
  failed: boolean
  items: SearchItem[]
}

export interface SearchEnvelope {
  q: string
  partial: boolean
  /** SEMPRE os seis, na ordem do contrato — mesmo vazios. */
  groups: SearchGroup[]
}

/**
 * O teto de ids por pedido, imposto pelo servidor (400 acima disso).
 *
 * O histórico guarda 7, portanto folgadíssimo — a constante está aqui para que
 * o dia em que alguém subir `SEARCH_HISTORY_MAX` haja um sítio que diga em voz
 * alta qual é o teto do outro lado do fio.
 */
export const MAX_SEARCH_IDS = 20

const acceptJson = { Accept: 'application/json' }

/**
 * `GET /api/search?q=` — a busca por texto.
 *
 * ⚠️ O `signal` NÃO é opcional por conveniência: sem ele, respostas fora de
 * ordem repintam a lista com o resultado de um termo antigo. Quem chama tem de
 * o passar; ele fica opcional na assinatura só porque o `fetch` o aceita
 * ausente, e a paleta passa-o sempre.
 */
export async function searchGlobal(q: string, signal?: AbortSignal): Promise<SearchEnvelope> {
  const url = new URL(`${API_BASE}/search`, window.location.origin)
  url.searchParams.set('q', q)

  const init: RequestInit = { credentials: 'include', headers: acceptJson }
  if (signal) init.signal = signal

  const res = await fetch(`${url.pathname}${url.search}`, init)
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível buscar agora (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as SearchEnvelope
}

/**
 * `GET /api/search?ids=` — resolver o histórico.
 *
 * Os `refs` já vêm no formato `kind:id` (é assim que o histórico os guarda), e
 * a serialização é a ingénua: vírgulas. O servidor ignora pedaços vazios e
 * responde 400 a qualquer outra coisa malformada — de propósito, para um erro
 * de escrita doer aqui em vez de desaparecer.
 *
 * ⚠️ Neste modo NÃO há corte de 5 por grupo, e `hasMore` vem sempre `false`. O
 * teto é o próprio `ids`. Se houvesse corte, sete recentes todos do mesmo tipo
 * perdiam duas linhas sem aviso.
 */
export async function resolveSearchIds(
  refs: readonly string[],
  signal?: AbortSignal,
): Promise<SearchEnvelope> {
  const url = new URL(`${API_BASE}/search`, window.location.origin)
  url.searchParams.set('ids', refs.slice(0, MAX_SEARCH_IDS).join(','))

  const init: RequestInit = { credentials: 'include', headers: acceptJson }
  if (signal) init.signal = signal

  const res = await fetch(`${url.pathname}${url.search}`, init)
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível carregar os recentes (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as SearchEnvelope
}

/**
 * Achata os grupos numa lista única, NA ORDEM EM QUE O SERVIDOR OS MANDOU.
 *
 * É esta lista que o teclado percorre: o agrupamento é visual, e navegar nunca
 * deve exigir que se saiba onde um grupo acaba.
 */
export function flattenGroups(groups: readonly SearchGroup[]): SearchItem[] {
  return groups.flatMap((g) => g.items)
}
